import { createContext, useContext, useState, useEffect } from "react";

const LanguageContext = createContext();

// Translation dictionaries
export const translations = {
  en: {
    // Common
    search: "Search",
    export: "Export",
    download: "Download",
    save: "Save",
    cancel: "Cancel",
    delete: "Delete",
    edit: "Edit",
    add: "Add",
    new: "New",
    filter: "Filter",
    filters: "Filters",
    clear: "Clear",
    apply: "Apply",
    close: "Close",
    view: "View",
    viewAll: "View all",
    live: "Live",
    settings: "Settings",
    help: "Help",
    
    // Navigation
    dashboard: "Dashboard",
    search: "Search",
    ontologyBuilder: "Ontology Builder",
    graphExplorer: "Graph Explorer",
    gisMapView: "GIS",
    timelineAnalytics: "Timeline Analytics",
    searchResults: "Search Results",
    worksheetBuilder: "Worksheet Builder",
    alertsReports: "Alerts & Reports",
    adminSettings: "Admin Settings",
    designSystem: "Design System",
    downloads: "Downloads",
    downloadsDescription: "Download the source code and themes for DataForge Analytics.",
    download: "Download",
    directLinks: "Direct Download Links",
    
    // Authentication
    welcomeBack: "Welcome Back",
    signInToContinue: "Sign in to continue to DataForge Analytics",
    username: "Username",
    password: "Password",
    enterUsername: "Enter your username",
    enterPassword: "Enter your password",
    signIn: "Sign In",
    signingIn: "Signing in...",
    signOut: "Sign Out",
    secureConnection: "Your connection is secure and encrypted",
    authenticated: "Authenticated",
    
    // Dashboard
    analyticsOverview: "Analytics Overview",
    realTimeInsights: "Real-time insights across all your data",
    totalEntities: "Total Entities",
    relationships: "Relationships",
    activeQueries: "Active Queries",
    alertsToday: "Alerts Today",
    dataGrowthTrends: "Data Growth Trends",
    entitiesAndRelationships: "Entities and relationships over time",
    entityDistribution: "Entity Distribution",
    breakdownByType: "Breakdown by type",
    recentActivity: "Recent Activity",
    latestActions: "Latest actions",
    activeProjects: "Active Projects",
    yourInvestigations: "Your investigations",
    editModeEnabled: "Edit mode enabled. Drag widgets to reorder them.",
    savedSearches: "Saved Searches",
    savedSearchWidget: "Quick access to your saved search form",
    openSearch: "Open Search",
    fields: "fields",
    moreFields: "more fields",
    enter: "Enter",
    widgetAdded: "Widget added to Dashboard!",
    goToDashboardToView: "Go to Dashboard to view your saved search form",
    
    // Graph Explorer imports
    entitiesImported: "entities imported",
    entitiesAddedToGraph: "entities added to graph",
    importedEntities: "Imported Entities",
    addToGraphNow: "Add to Graph",
    clearImport: "Clear",
    
    // GIS Map
    searchLocations: "Search locations...",
    mapLayers: "Map Layers",
    markers: "Markers",
    intensityCircles: "Intensity Circles",
    connections: "Connections",
    mapStyle: "Map Style",
    dark: "Dark",
    light: "Light",
    satellite: "Satellite",
    terrain: "Terrain",
    legend: "Legend",
    locations: "Locations",
    events: "Events",
    hotspots: "Hotspots",
    drawRegion: "Draw Region",
    locationDetails: "Location Details",
    coordinates: "Coordinates",
    latitude: "Latitude",
    longitude: "Longitude",
    activityMetrics: "Activity Metrics",
    intensityScore: "Intensity Score",
    totalEvents: "Total Events",
    lastActivity: "Last Activity",
    recentEvents: "Recent Events",
    directions: "Directions",
    focus: "Focus",
    zoomIn: "Zoom In",
    zoomOut: "Zoom Out",
    resetView: "Reset View",
    fullscreen: "Fullscreen",
    measureDistance: "Measure Distance",
    drawPolygon: "Draw Polygon",
    clearDrawings: "Clear Drawings",
    clickToSetPoints: "Click to set points",
    points: "points",
    clustering: "Clustering",
    editMode: "Edit Mode",
    editModeActive: "Edit mode - drag markers to move",
    dragToMove: "Drag to move location",
    today: "Today",
    organization: "Organization",
    location: "Location",
    hotspot: "Hotspot",
    hoursAgo: "hours ago",
    
    // Graph Explorer
    nodeTypes: "Node Types",
    allTypes: "All Types",
    person: "Person",
    document: "Document",
    event: "Event",
    layoutOptions: "Layout Options",
    physics: "Physics",
    hierarchical: "Hierarchical",
    circular: "Circular",
    graphStats: "Graph Statistics",
    nodes: "Nodes",
    edges: "Edges",
    clusters: "Clusters",
    cluster: "Cluster",
    selectedNode: "Selected Node",
    connectionStrength: "Connection Strength",
    fitToScreen: "Fit to Screen",
    exportGraph: "Export Graph",
    addNode: "Add Node",
    removeNode: "Remove Node",
    nodeDetails: "Node Details",
    editNode: "Edit Node",
    editNodeDescription: "Modify node properties and details",
    connected: "Connected",
    expand: "Expand",
    paths: "Paths",
    searchNodes: "Search nodes...",
    zoom: "Zoom",
    pan: "Pan",
    select: "Select",
    relation: "Relation",
    relationDetails: "Relation Details",
    editRelation: "Edit Relation",
    editRelationDescription: "Modify the relation label and properties",
    deleteRelation: "Delete Relation",
    relationLabel: "Relation Label",
    configureRelationProperties: "Configure relation label and properties",
    animated: "Animated",
    showFlowAnimation: "Show flow animation",
    lineColor: "Line Color",
    connection: "Connection",
    
    // Ontology Builder
    entityTypes: "Entity Types",
    entities: "Entities",
    relations: "Relations",
    undo: "Undo",
    validate: "Validate",
    configureEntity: "Configure Entity",
    configurePropertiesValidations: "Configure properties and validations",
    properties: "Properties",
    currentProperties: "Current Properties",
    addProperty: "Add Property",
    propertyName: "Property Name",
    propertyType: "Property Type",
    displayName: "Display Name",
    deleteEntity: "Delete Entity",
    
    // Alerts & Reports
    monitoringCenter: "Monitoring Center",
    realTimeAlerts: "Real-time alerts and report management",
    critical: "Critical",
    highPriority: "High Priority",
    totalActive: "Total Active",
    reportsToday: "Reports Today",
    alerts: "Alerts",
    reports: "Reports",
    alertRules: "Alert Rules",
    severity: "Severity",
    status: "Status",
    all: "All",
    high: "High",
    medium: "Medium",
    low: "Low",
    active: "Active",
    investigating: "Investigating",
    resolved: "Resolved",
    searchAlerts: "Search alerts...",
    affectedEntities: "Affected Entities",
    graph: "Graph",
    timeline: "Timeline",
    investigate: "Investigate",
    reportName: "Report Name",
    type: "Type",
    lastRun: "Last Run",
    records: "Records",
    actions: "Actions",
    completed: "Completed",
    running: "Running",
    alertRulesConfig: "Alert Rules Configuration",
    manageAutomatedAlerts: "Manage automated alert triggers and conditions",
    triggers: "triggers",
    newAlertRule: "New Alert Rule",
    
    // Admin Settings
    systemConfig: "System configuration and performance monitoring",
    performance: "Performance",
    services: "Services",
    logs: "Logs",
    cpuUsage: "CPU Usage",
    memory: "Memory",
    requestsMin: "Requests/min",
    avgLatency: "Avg Latency",
    resourceUsage: "Resource Usage (24h)",
    cpuMemoryUtilization: "CPU and memory utilization over time",
    requestVolume: "Request Volume (24h)",
    apiRequestsPerHour: "API requests per hour",
    systemServices: "System Services",
    healthStatus: "Health status and performance metrics",
    uptime: "Uptime",
    latency: "Latency",
    healthy: "Healthy",
    warning: "Warning",
    systemLogs: "System Logs",
    realTimeAppLogs: "Real-time application logs and events",
    allLevels: "All Levels",
    error: "Error",
    info: "Info",
    debug: "Debug",
    allServices: "All Services",
    generalSettings: "General Settings",
    basicSystemConfig: "Basic system configuration",
    maintenanceMode: "Maintenance Mode",
    disableSystemAccess: "Disable system access for users",
    debugLogging: "Debug Logging",
    enableVerboseLogging: "Enable verbose logging",
    autoBackup: "Auto-backup",
    dailyBackups: "Daily automatic backups",
    performanceTuning: "Performance Tuning",
    optimizePerformance: "Optimize system performance",
    cacheTTL: "Cache TTL",
    queryTimeout: "Query Timeout",
    maxConcurrentRequests: "Max Concurrent Requests",
    security: "Security",
    securityAccessControls: "Security and access controls",
    twoFactorAuth: "Two-Factor Auth",
    require2FA: "Require 2FA for all users",
    ipWhitelisting: "IP Whitelisting",
    restrictByIP: "Restrict access by IP",
    auditLogging: "Audit Logging",
    logAllActions: "Log all user actions",
    integrations: "Integrations",
    externalServices: "External service connections",
    database: "Database",
    searchEngine: "Search Engine",
    objectStorage: "Object Storage",
    connected: "Connected",
    
    // Search Results
    searchFor: "Search for",
    results: "Results",
    relevance: "Relevance",
    dateDesc: "Date (Newest)",
    dateAsc: "Date (Oldest)",
    entityType: "Entity Type",
    dateRange: "Date Range",
    source: "Source",
    anyType: "Any Type",
    anyTime: "Any Time",
    lastDay: "Last 24 Hours",
    lastWeek: "Last Week",
    lastMonth: "Last Month",
    lastYear: "Last Year",
    anySource: "Any Source",
    internalDB: "Internal Database",
    externalAPI: "External API",
    fileImport: "File Import",
    clearFilters: "Clear Filters",
    clearForm: "Clear Form",
    noResults: "No results found",
    tryDifferentSearch: "Try a different search term or adjust filters",
    advancedSearch: "Advanced Search",
    searchDescription: "Search across all entity types with specific attributes",
    enterCriteria: "Enter search criteria to find",
    searchResultsFor: "Search results for",
    found: "found",
    backToSearch: "Back to Search",
    tryDifferentCriteria: "Try adjusting your search criteria",
    home: "Home",
    name: "Name",
    role: "Role",
    company: "Company",
    email: "Email",
    phone: "Phone",
    industry: "Industry",
    employees: "Employees",
    revenue: "Revenue",
    founded: "Founded",
    headquarters: "Headquarters",
    address: "Address",
    city: "City",
    country: "Country",
    capacity: "Capacity",
    date: "Date",
    attendees: "Attendees",
    organizer: "Organizer",
    author: "Author",
    department: "Department",
    pages: "Pages",
    created: "Created",
    title: "Title",
    mandatoryFields: "Mandatory fields",
    customizeFields: "Customize Fields",
    selectFields: "Select Fields",
    quickFilter: "Quick filter...",
    viewDetails: "View Details",
    gallery: "Gallery",
    images: "images",
    quickActions: "Quick Actions",
    share: "Share",
    print: "Print",
    exportPDF: "Export PDF",
    contactInformation: "Contact Information",
    organizationInfo: "Organization Info",
    locationInfo: "Location Info",
    financialInfo: "Financial Info",
    dates: "Dates",
    additionalInfo: "Additional Information",
    details: "Details",
    firstName: "First Name",
    lastName: "Last Name",
    mobile: "Mobile",
    nationality: "Nationality",
    gender: "Gender",
    education: "Education",
    yearsExperience: "Years Experience",
    clearanceLevel: "Clearance Level",
    linkedIn: "LinkedIn",
    legalName: "Legal Name",
    sector: "Sector",
    stockSymbol: "Stock Symbol",
    marketCap: "Market Cap",
    ceo: "CEO",
    website: "Website",
    taxId: "Tax ID",
    rating: "Rating",
    state: "State/Province",
    postalCode: "Postal Code",
    region: "Region",
    sqFootage: "Square Footage",
    floors: "Floors",
    securityLevel: "Security Level",
    accessibility: "Accessibility",
    endDate: "End Date",
    venue: "Venue",
    budget: "Budget",
    ticketPrice: "Ticket Price",
    category: "Category",
    format: "Format",
    speakers: "Speakers",
    sponsors: "Sponsors",
    classification: "Classification",
    version: "Version",
    fileSize: "File Size",
    modified: "Modified",
    accessLevel: "Access Level",
    selectAll: "Select All",
    deselectAll: "Deselect All",
    addToDashboard: "Add to Dashboard",
    addToGraph: "Add to Graph",
    selected: "selected",
    showing: "Showing",
    of: "of",
    tags: "Tags",
    language: "Language",
    
    // Worksheet Builder
    widgetPalette: "Widget Palette",
    charts: "Charts",
    tables: "Tables",
    metrics: "Metrics",
    text: "Text",
    worksheetCanvas: "Worksheet Canvas",
    dropWidgetsHere: "Drop widgets here to build your worksheet",
    worksheetSettings: "Worksheet Settings",
    title: "Title",
    description: "Description",
    refreshInterval: "Refresh Interval",
    manual: "Manual",
    everyMinute: "Every minute",
    every5Minutes: "Every 5 minutes",
    every15Minutes: "Every 15 minutes",
    
    // Timeline
    timelineExplorer: "Timeline Explorer",
    eventHistory: "Event history and temporal analysis",
    eventFilters: "Event Filters",
    categories: "Categories",
    communications: "Communications",
    transactions: "Transactions",
    meetings: "Meetings",
    filings: "Filings",
    timelineView: "Timeline View",
    
    // Design System
    designSystemTitle: "Design System",
    uiComponentLibrary: "DataForge UI Component Library & Style Guide",
    theme: "Theme",
    exportPDF: "Export PDF",
    brandLogo: "Brand & Logo",
    logoVariations: "Logo variations and usage guidelines",
    fullLogo: "Full Logo (Dark BG)",
    iconOnly: "Icon Only",
    largeIcon: "Large Icon",
    sizeVariants: "Size Variants",
    colorPalette: "Color Palette",
    semanticColors: "Semantic color tokens for consistent theming",
    chartColors: "Chart Colors",
    backgroundForeground: "Background & Foreground",
    typography: "Typography",
    fontFamilies: "Font families, sizes, and weights",
    buttons: "Buttons",
    buttonVariants: "Button variants and states",
    primary: "Primary",
    secondary: "Secondary",
    outline: "Outline",
    ghost: "Ghost",
    destructive: "Destructive",
    link: "Link",
    small: "Small",
    default: "Default",
    large: "Large",
    withIcon: "With Icon",
    disabled: "Disabled",
    fullWidth: "Full Width (Mobile)",
    formControls: "Form Controls",
    inputFields: "Input fields, selects, and other form elements",
    textInput: "Text Input",
    searchInput: "Search Input",
    select: "Select",
    selectOption: "Select option",
    option: "Option",
    textarea: "Textarea",
    enterDescription: "Enter description...",
    checkbox: "Checkbox",
    switch: "Switch",
    slider: "Slider",
    badgesStatus: "Badges & Status Indicators",
    visualIndicators: "Visual indicators for status and categories",
    success: "Success",
    activeOnline: "Active / Online",
    warningPending: "Warning / Pending",
    errorOffline: "Error / Offline",
    inactiveDisabled: "Inactive / Disabled",
    alertsNotifications: "Alerts & Notifications",
    contextualFeedback: "Contextual feedback messages",
    information: "Information",
    informationalAlert: "This is an informational alert message.",
    operationCompleted: "Operation completed successfully.",
    pleaseReview: "Please review before proceeding.",
    somethingWrong: "Something went wrong. Please try again.",
    cardsContainers: "Cards & Containers",
    contentContainers: "Content containers and card layouts",
    basicCard: "Basic Card",
    simpleCard: "Simple card with header and content.",
    highlighted: "Highlighted",
    cardAccentBorder: "Card with accent border.",
    tinted: "Tinted",
    cardTintedBg: "Card with tinted background.",
    progressLoading: "Progress & Loading",
    progressIndicators: "Progress indicators and loading states",
    defaultProgress: "Default Progress",
    loading: "Loading...",
    processing: "Processing",
    avatars: "Avatars",
    userAvatars: "User avatars and profile images",
    spacingScale: "Spacing Scale",
    consistentSpacing: "Consistent spacing using Tailwind scale",
    icons: "Icons",
    fontAwesomeIcons: "FontAwesome icons used throughout the application",
    version: "DataForge Design System v1.0",
    builtWith: "Built with React, Tailwind CSS, and shadcn/ui",
    
    // Notifications
    notifications: "Notifications",
    criticalAlert: "Critical Alert",
    anomalyDetected: "Anomaly detected in network traffic patterns",
    dataImportComplete: "Data Import Complete",
    recordsProcessed: "2.4M records processed successfully",
    reportGenerated: "Report Generated",
    weeklyReportReady: "Weekly analytics report is ready for review",
    viewAllNotifications: "View all notifications",
    
    // Quick Actions
    newOntology: "New Ontology",
    newWorksheet: "New Worksheet",
    importData: "Import Data",
    newProject: "New Project",
    
    // Alerts & Reports
    monitoringCenter: "Monitoring Center",
    realTimeAlerts: "Real-time alerts and report management",
    newAlertRule: "New Alert Rule",
    highPriority: "High Priority",
    totalActive: "Total Active",
    reportsToday: "Reports Today",
    alerts: "Alerts",
    reports: "Reports",
    alertRules: "Alert Rules",
    severity: "Severity",
    all: "All",
    high: "High",
    medium: "Medium",
    low: "Low",
    active: "Active",
    investigating: "Investigating",
    resolved: "Resolved",
    searchAlerts: "Search alerts...",
    affectedEntities: "Affected Entities",
    graph: "Graph",
    investigate: "Investigate",
    reportName: "Report Name",
    lastRun: "Last Run",
    records: "Records",
    actions: "Actions",
    alertRulesConfig: "Alert Rules Configuration",
    manageAutomatedAlerts: "Manage automated alert triggers and conditions",
    triggers: "triggers",
    completed: "completed",
    running: "running",
    
    // Admin Settings
    systemConfig: "System configuration and performance monitoring",
    performance: "Performance",
    cpuUsage: "CPU Usage",
    requestsMin: "Requests/min",
    avgLatency: "Avg Latency",
    healthy: "Healthy",
    resourceUsage: "Resource Usage (24h)",
    cpuMemoryUtilization: "CPU and memory utilization over time",
    requestVolume: "Request Volume (24h)",
    apiRequestsPerHour: "API requests per hour",
    systemServices: "System Services",
    healthStatus: "Health status and performance metrics",
    uptime: "Uptime",
    latency: "Latency",
    systemLogs: "System Logs",
    realTimeAppLogs: "Real-time application logs and events",
    warning: "Warning",
    
    // Language
    language: "Language",
    english: "English",
    german: "German",
    
    // Misc
    enterText: "Enter text...",
    minutes: "minutes",
    seconds: "seconds",
    hours: "hours",
    months: "months",
  },
  de: {
    // Common
    search: "Suchen",
    export: "Exportieren",
    download: "Herunterladen",
    save: "Speichern",
    cancel: "Abbrechen",
    delete: "Löschen",
    edit: "Bearbeiten",
    add: "Hinzufügen",
    new: "Neu",
    filter: "Filtern",
    filters: "Filter",
    clear: "Löschen",
    apply: "Anwenden",
    close: "Schließen",
    view: "Ansicht",
    viewAll: "Alle anzeigen",
    live: "Live",
    settings: "Einstellungen",
    help: "Hilfe",
    
    // Navigation
    dashboard: "Dashboard",
    search: "Suche",
    ontologyBuilder: "Ontologie-Builder",
    graphExplorer: "Graph-Explorer",
    gisMapView: "GIS",
    timelineAnalytics: "Zeitleisten-Analytik",
    searchResults: "Suchergebnisse",
    worksheetBuilder: "Arbeitsblatt-Builder",
    alertsReports: "Warnungen & Berichte",
    adminSettings: "Admin-Einstellungen",
    designSystem: "Design-System",
    downloads: "Downloads",
    downloadsDescription: "Laden Sie den Quellcode und die Themes für DataForge Analytics herunter.",
    download: "Herunterladen",
    directLinks: "Direkte Download-Links",
    
    // Authentication
    welcomeBack: "Willkommen zurück",
    signInToContinue: "Melden Sie sich an, um zu DataForge Analytics fortzufahren",
    username: "Benutzername",
    password: "Passwort",
    enterUsername: "Geben Sie Ihren Benutzernamen ein",
    enterPassword: "Geben Sie Ihr Passwort ein",
    signIn: "Anmelden",
    signingIn: "Anmeldung...",
    signOut: "Abmelden",
    secureConnection: "Ihre Verbindung ist sicher und verschlüsselt",
    authenticated: "Authentifiziert",
    
    // Dashboard
    analyticsOverview: "Analytik-Übersicht",
    realTimeInsights: "Echtzeit-Einblicke in alle Ihre Daten",
    totalEntities: "Gesamte Entitäten",
    relationships: "Beziehungen",
    activeQueries: "Aktive Abfragen",
    alertsToday: "Warnungen heute",
    dataGrowthTrends: "Datenwachstumstrends",
    entitiesAndRelationships: "Entitäten und Beziehungen im Zeitverlauf",
    entityDistribution: "Entitätsverteilung",
    breakdownByType: "Aufschlüsselung nach Typ",
    recentActivity: "Letzte Aktivität",
    latestActions: "Neueste Aktionen",
    activeProjects: "Aktive Projekte",
    yourInvestigations: "Ihre Untersuchungen",
    editModeEnabled: "Bearbeitungsmodus aktiviert. Widgets zum Neuordnen ziehen.",
    savedSearches: "Gespeicherte Suchen",
    savedSearchWidget: "Schnellzugriff auf Ihr gespeichertes Suchformular",
    openSearch: "Suche öffnen",
    fields: "Felder",
    moreFields: "weitere Felder",
    enter: "Eingeben",
    widgetAdded: "Widget zum Dashboard hinzugefügt!",
    goToDashboardToView: "Gehen Sie zum Dashboard, um Ihr gespeichertes Suchformular anzuzeigen",
    
    // Graph Explorer imports
    entitiesImported: "Entitäten importiert",
    entitiesAddedToGraph: "Entitäten zum Graphen hinzugefügt",
    importedEntities: "Importierte Entitäten",
    addToGraphNow: "Zum Graphen hinzufügen",
    clearImport: "Löschen",
    
    // GIS Map
    searchLocations: "Standorte suchen...",
    mapLayers: "Kartenebenen",
    markers: "Markierungen",
    intensityCircles: "Intensitätskreise",
    connections: "Verbindungen",
    mapStyle: "Kartenstil",
    dark: "Dunkel",
    light: "Hell",
    satellite: "Satellit",
    terrain: "Gelände",
    legend: "Legende",
    locations: "Standorte",
    events: "Ereignisse",
    hotspots: "Hotspots",
    drawRegion: "Region zeichnen",
    locationDetails: "Standortdetails",
    coordinates: "Koordinaten",
    latitude: "Breitengrad",
    longitude: "Längengrad",
    activityMetrics: "Aktivitätsmetriken",
    intensityScore: "Intensitätswert",
    totalEvents: "Gesamtereignisse",
    lastActivity: "Letzte Aktivität",
    recentEvents: "Letzte Ereignisse",
    directions: "Wegbeschreibung",
    focus: "Fokus",
    zoomIn: "Vergrößern",
    zoomOut: "Verkleinern",
    resetView: "Ansicht zurücksetzen",
    fullscreen: "Vollbild",
    measureDistance: "Entfernung messen",
    drawPolygon: "Polygon zeichnen",
    clearDrawings: "Zeichnungen löschen",
    clickToSetPoints: "Klicken zum Setzen",
    points: "Punkte",
    clustering: "Clustering",
    editMode: "Bearbeitungsmodus",
    editModeActive: "Bearbeitungsmodus - Marker ziehen zum Verschieben",
    dragToMove: "Ziehen zum Verschieben",
    today: "Heute",
    organization: "Organisation",
    location: "Standort",
    hotspot: "Hotspot",
    hoursAgo: "Stunden her",
    
    // Graph Explorer
    nodeTypes: "Knotentypen",
    allTypes: "Alle Typen",
    person: "Person",
    document: "Dokument",
    event: "Ereignis",
    layoutOptions: "Layout-Optionen",
    physics: "Physik",
    hierarchical: "Hierarchisch",
    circular: "Kreisförmig",
    graphStats: "Graph-Statistiken",
    nodes: "Knoten",
    edges: "Kanten",
    clusters: "Cluster",
    cluster: "Gruppieren",
    selectedNode: "Ausgewählter Knoten",
    connectionStrength: "Verbindungsstärke",
    fitToScreen: "An Bildschirm anpassen",
    exportGraph: "Graph exportieren",
    addNode: "Knoten hinzufügen",
    removeNode: "Knoten entfernen",
    nodeDetails: "Knotendetails",
    editNode: "Knoten bearbeiten",
    editNodeDescription: "Knoteneigenschaften und Details ändern",
    connected: "Verbunden",
    expand: "Erweitern",
    paths: "Pfade",
    searchNodes: "Knoten suchen...",
    zoom: "Zoom",
    pan: "Verschieben",
    select: "Auswählen",
    relation: "Beziehung",
    relationDetails: "Beziehungsdetails",
    editRelation: "Beziehung bearbeiten",
    editRelationDescription: "Beziehungslabel und Eigenschaften ändern",
    deleteRelation: "Beziehung löschen",
    relationLabel: "Beziehungslabel",
    configureRelationProperties: "Beziehungslabel und Eigenschaften konfigurieren",
    animated: "Animiert",
    showFlowAnimation: "Flussanimation anzeigen",
    lineColor: "Linienfarbe",
    connection: "Verbindung",
    
    // Ontology Builder
    entityTypes: "Entitätstypen",
    entities: "Entitäten",
    relations: "Relationen",
    undo: "Rückgängig",
    validate: "Validieren",
    configureEntity: "Entität konfigurieren",
    configurePropertiesValidations: "Eigenschaften und Validierungen konfigurieren",
    properties: "Eigenschaften",
    currentProperties: "Aktuelle Eigenschaften",
    addProperty: "Eigenschaft hinzufügen",
    propertyName: "Eigenschaftsname",
    propertyType: "Eigenschaftstyp",
    displayName: "Anzeigename",
    deleteEntity: "Entität löschen",
    
    // Alerts & Reports
    monitoringCenter: "Überwachungszentrum",
    realTimeAlerts: "Echtzeit-Warnungen und Berichtsverwaltung",
    critical: "Kritisch",
    highPriority: "Hohe Priorität",
    totalActive: "Gesamt aktiv",
    reportsToday: "Berichte heute",
    alerts: "Warnungen",
    reports: "Berichte",
    alertRules: "Warnregeln",
    severity: "Schweregrad",
    status: "Status",
    all: "Alle",
    high: "Hoch",
    medium: "Mittel",
    low: "Niedrig",
    active: "Aktiv",
    investigating: "In Untersuchung",
    resolved: "Gelöst",
    searchAlerts: "Warnungen suchen...",
    affectedEntities: "Betroffene Entitäten",
    graph: "Graph",
    timeline: "Zeitleiste",
    investigate: "Untersuchen",
    reportName: "Berichtsname",
    type: "Typ",
    lastRun: "Letzter Lauf",
    records: "Datensätze",
    actions: "Aktionen",
    completed: "Abgeschlossen",
    running: "Läuft",
    alertRulesConfig: "Warnregeln-Konfiguration",
    manageAutomatedAlerts: "Automatisierte Warn-Trigger und Bedingungen verwalten",
    triggers: "Auslöser",
    newAlertRule: "Neue Warnregel",
    
    // Admin Settings
    systemConfig: "Systemkonfiguration und Leistungsüberwachung",
    performance: "Leistung",
    services: "Dienste",
    logs: "Protokolle",
    cpuUsage: "CPU-Auslastung",
    memory: "Speicher",
    requestsMin: "Anfragen/Min",
    avgLatency: "Durchschn. Latenz",
    resourceUsage: "Ressourcennutzung (24h)",
    cpuMemoryUtilization: "CPU- und Speicherauslastung im Zeitverlauf",
    requestVolume: "Anfragevolumen (24h)",
    apiRequestsPerHour: "API-Anfragen pro Stunde",
    systemServices: "Systemdienste",
    healthStatus: "Zustandsstatus und Leistungsmetriken",
    uptime: "Betriebszeit",
    latency: "Latenz",
    healthy: "Gesund",
    warning: "Warnung",
    systemLogs: "Systemprotokolle",
    realTimeAppLogs: "Echtzeit-Anwendungsprotokolle und Ereignisse",
    allLevels: "Alle Ebenen",
    error: "Fehler",
    info: "Info",
    debug: "Debug",
    allServices: "Alle Dienste",
    generalSettings: "Allgemeine Einstellungen",
    basicSystemConfig: "Grundlegende Systemkonfiguration",
    maintenanceMode: "Wartungsmodus",
    disableSystemAccess: "Systemzugriff für Benutzer deaktivieren",
    debugLogging: "Debug-Protokollierung",
    enableVerboseLogging: "Ausführliche Protokollierung aktivieren",
    autoBackup: "Auto-Backup",
    dailyBackups: "Tägliche automatische Backups",
    performanceTuning: "Leistungsoptimierung",
    optimizePerformance: "Systemleistung optimieren",
    cacheTTL: "Cache-TTL",
    queryTimeout: "Abfrage-Timeout",
    maxConcurrentRequests: "Max. gleichzeitige Anfragen",
    security: "Sicherheit",
    securityAccessControls: "Sicherheit und Zugriffskontrollen",
    twoFactorAuth: "Zwei-Faktor-Auth",
    require2FA: "2FA für alle Benutzer erforderlich",
    ipWhitelisting: "IP-Whitelist",
    restrictByIP: "Zugriff nach IP einschränken",
    auditLogging: "Audit-Protokollierung",
    logAllActions: "Alle Benutzeraktionen protokollieren",
    integrations: "Integrationen",
    externalServices: "Externe Dienstverbindungen",
    database: "Datenbank",
    searchEngine: "Suchmaschine",
    objectStorage: "Objektspeicher",
    connected: "Verbunden",
    
    // Search Results
    searchFor: "Suchen nach",
    results: "Ergebnisse",
    relevance: "Relevanz",
    dateDesc: "Datum (Neueste)",
    dateAsc: "Datum (Älteste)",
    entityType: "Entitätstyp",
    dateRange: "Datumsbereich",
    source: "Quelle",
    anyType: "Beliebiger Typ",
    anyTime: "Beliebige Zeit",
    lastDay: "Letzte 24 Stunden",
    lastWeek: "Letzte Woche",
    lastMonth: "Letzter Monat",
    lastYear: "Letztes Jahr",
    anySource: "Beliebige Quelle",
    internalDB: "Interne Datenbank",
    externalAPI: "Externe API",
    fileImport: "Dateiimport",
    clearFilters: "Filter löschen",
    clearForm: "Formular löschen",
    noResults: "Keine Ergebnisse gefunden",
    tryDifferentSearch: "Versuchen Sie einen anderen Suchbegriff oder passen Sie die Filter an",
    advancedSearch: "Erweiterte Suche",
    searchDescription: "Suchen Sie über alle Entitätstypen mit bestimmten Attributen",
    enterCriteria: "Geben Sie Suchkriterien ein, um zu finden",
    searchResultsFor: "Suchergebnisse für",
    found: "gefunden",
    backToSearch: "Zurück zur Suche",
    tryDifferentCriteria: "Versuchen Sie, Ihre Suchkriterien anzupassen",
    home: "Startseite",
    name: "Name",
    role: "Rolle",
    company: "Unternehmen",
    email: "E-Mail",
    phone: "Telefon",
    industry: "Branche",
    employees: "Mitarbeiter",
    revenue: "Umsatz",
    founded: "Gegründet",
    headquarters: "Hauptsitz",
    address: "Adresse",
    city: "Stadt",
    country: "Land",
    capacity: "Kapazität",
    date: "Datum",
    attendees: "Teilnehmer",
    organizer: "Veranstalter",
    author: "Autor",
    department: "Abteilung",
    pages: "Seiten",
    created: "Erstellt",
    title: "Titel",
    description: "Beschreibung",
    refreshInterval: "Aktualisierungsintervall",
    manual: "Manuell",
    everyMinute: "Jede Minute",
    every5Minutes: "Alle 5 Minuten",
    every15Minutes: "Alle 15 Minuten",
    
    // Timeline
    timelineExplorer: "Zeitleisten-Explorer",
    eventHistory: "Ereignisverlauf und zeitliche Analyse",
    eventFilters: "Ereignisfilter",
    categories: "Kategorien",
    communications: "Kommunikation",
    transactions: "Transaktionen",
    meetings: "Meetings",
    filings: "Einreichungen",
    timelineView: "Zeitleistenansicht",
    
    // Design System
    designSystemTitle: "Design-System",
    uiComponentLibrary: "DataForge UI-Komponentenbibliothek & Stil-Leitfaden",
    theme: "Theme",
    exportPDF: "PDF exportieren",
    brandLogo: "Marke & Logo",
    logoVariations: "Logo-Varianten und Nutzungsrichtlinien",
    fullLogo: "Vollständiges Logo (Dunkler HG)",
    iconOnly: "Nur Symbol",
    largeIcon: "Großes Symbol",
    sizeVariants: "Größenvarianten",
    colorPalette: "Farbpalette",
    semanticColors: "Semantische Farb-Tokens für konsistentes Theming",
    chartColors: "Diagrammfarben",
    backgroundForeground: "Hintergrund & Vordergrund",
    typography: "Typografie",
    fontFamilies: "Schriftfamilien, -größen und -stärken",
    buttons: "Schaltflächen",
    buttonVariants: "Schaltflächenvarianten und -zustände",
    primary: "Primär",
    secondary: "Sekundär",
    outline: "Umriss",
    ghost: "Geist",
    destructive: "Destruktiv",
    link: "Link",
    small: "Klein",
    default: "Standard",
    large: "Groß",
    withIcon: "Mit Symbol",
    disabled: "Deaktiviert",
    fullWidth: "Volle Breite (Mobil)",
    formControls: "Formularsteuerungen",
    inputFields: "Eingabefelder, Auswahlmenüs und andere Formularelemente",
    textInput: "Texteingabe",
    searchInput: "Sucheingabe",
    select: "Auswahl",
    selectOption: "Option auswählen",
    option: "Option",
    textarea: "Textbereich",
    enterDescription: "Beschreibung eingeben...",
    checkbox: "Kontrollkästchen",
    switch: "Schalter",
    slider: "Schieberegler",
    badgesStatus: "Abzeichen & Statusanzeigen",
    visualIndicators: "Visuelle Indikatoren für Status und Kategorien",
    success: "Erfolg",
    activeOnline: "Aktiv / Online",
    warningPending: "Warnung / Ausstehend",
    errorOffline: "Fehler / Offline",
    inactiveDisabled: "Inaktiv / Deaktiviert",
    alertsNotifications: "Warnungen & Benachrichtigungen",
    contextualFeedback: "Kontextbezogene Feedback-Nachrichten",
    information: "Information",
    informationalAlert: "Dies ist eine informative Warnmeldung.",
    operationCompleted: "Vorgang erfolgreich abgeschlossen.",
    pleaseReview: "Bitte überprüfen Sie vor dem Fortfahren.",
    somethingWrong: "Etwas ist schief gelaufen. Bitte versuchen Sie es erneut.",
    cardsContainers: "Karten & Container",
    contentContainers: "Inhaltscontainer und Kartenlayouts",
    basicCard: "Einfache Karte",
    simpleCard: "Einfache Karte mit Kopfzeile und Inhalt.",
    highlighted: "Hervorgehoben",
    cardAccentBorder: "Karte mit Akzentrand.",
    tinted: "Getönt",
    cardTintedBg: "Karte mit getöntem Hintergrund.",
    progressLoading: "Fortschritt & Laden",
    progressIndicators: "Fortschrittsanzeigen und Ladezustände",
    defaultProgress: "Standard-Fortschritt",
    loading: "Lädt...",
    processing: "Verarbeitung",
    avatars: "Avatare",
    userAvatars: "Benutzer-Avatare und Profilbilder",
    spacingScale: "Abstands-Skala",
    consistentSpacing: "Konsistenter Abstand mit Tailwind-Skala",
    icons: "Symbole",
    fontAwesomeIcons: "FontAwesome-Symbole in der Anwendung",
    version: "DataForge Design-System v1.0",
    builtWith: "Erstellt mit React, Tailwind CSS und shadcn/ui",
    
    // Notifications
    notifications: "Benachrichtigungen",
    criticalAlert: "Kritische Warnung",
    anomalyDetected: "Anomalie in Netzwerkverkehrsmustern erkannt",
    dataImportComplete: "Datenimport abgeschlossen",
    recordsProcessed: "2,4 Mio. Datensätze erfolgreich verarbeitet",
    reportGenerated: "Bericht erstellt",
    weeklyReportReady: "Wöchentlicher Analysebericht ist zur Überprüfung bereit",
    viewAllNotifications: "Alle Benachrichtigungen anzeigen",
    
    // Quick Actions
    newOntology: "Neue Ontologie",
    newWorksheet: "Neues Arbeitsblatt",
    importData: "Daten importieren",
    newProject: "Neues Projekt",
    
    // Alerts & Reports
    monitoringCenter: "Überwachungszentrum",
    realTimeAlerts: "Echtzeit-Warnungen und Berichtsverwaltung",
    newAlertRule: "Neue Warnregel",
    highPriority: "Hohe Priorität",
    totalActive: "Gesamt Aktiv",
    reportsToday: "Berichte Heute",
    alerts: "Warnungen",
    reports: "Berichte",
    alertRules: "Warnregeln",
    severity: "Schweregrad",
    all: "Alle",
    high: "Hoch",
    medium: "Mittel",
    low: "Niedrig",
    active: "Aktiv",
    investigating: "Untersuchen",
    resolved: "Gelöst",
    searchAlerts: "Warnungen suchen...",
    affectedEntities: "Betroffene Entitäten",
    graph: "Graph",
    investigate: "Untersuchen",
    reportName: "Berichtsname",
    lastRun: "Letzte Ausführung",
    records: "Datensätze",
    actions: "Aktionen",
    alertRulesConfig: "Konfiguration der Warnregeln",
    manageAutomatedAlerts: "Automatische Warntrigger und Bedingungen verwalten",
    triggers: "Auslöser",
    completed: "abgeschlossen",
    running: "läuft",
    
    // Admin Settings
    systemConfig: "Systemkonfiguration und Leistungsüberwachung",
    performance: "Leistung",
    cpuUsage: "CPU-Auslastung",
    requestsMin: "Anfragen/Min",
    avgLatency: "Durchschn. Latenz",
    healthy: "Gesund",
    resourceUsage: "Ressourcennutzung (24h)",
    cpuMemoryUtilization: "CPU- und Speicherauslastung im Zeitverlauf",
    requestVolume: "Anfragevolumen (24h)",
    apiRequestsPerHour: "API-Anfragen pro Stunde",
    systemServices: "Systemdienste",
    healthStatus: "Gesundheitsstatus und Leistungsmetriken",
    uptime: "Betriebszeit",
    latency: "Latenz",
    systemLogs: "Systemprotokolle",
    realTimeAppLogs: "Echtzeit-Anwendungsprotokolle und Ereignisse",
    warning: "Warnung",
    
    // Language
    language: "Sprache",
    english: "Englisch",
    german: "Deutsch",
    
    // Misc
    enterText: "Text eingeben...",
    minutes: "Minuten",
    seconds: "Sekunden",
    hours: "Stunden",
    months: "Monate",
  }
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(() => {
    const saved = localStorage.getItem("language");
    return saved || "en";
  });

  useEffect(() => {
    localStorage.setItem("language", language);
  }, [language]);

  const t = (key) => {
    return translations[language]?.[key] || translations.en[key] || key;
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error("useLanguage must be used within a LanguageProvider");
  }
  return context;
};
